import type { JSONContent } from '@tiptap/core';

export type FieldMeta = { label: string; sample: string };

/**
 * Parse a Jinja2 HTML string into tiptap JSON with mergeField nodes.
 *
 * Replaces `{{ varName }}` tokens with `<span data-type="mergeField" ...>` spans.
 * When a `fieldMap` is provided, each span gets `data-label` and `data-sample`
 * attributes so the editor can display human-readable info instead of the raw key.
 */
export function jinjaToHtml(
  jinja: string,
  fieldMap?: Record<string, FieldMeta>,
): string {
  return jinja.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key: string) => {
    const meta = fieldMap?.[key];
    const labelAttr = meta?.label ? ` data-label="${meta.label}"` : '';
    const sampleAttr = meta?.sample ? ` data-sample="${meta.sample}"` : '';
    return `<span data-type="mergeField" data-key="${key}"${labelAttr}${sampleAttr}>{{ ${key} }}</span>`;
  });
}

/**
 * Serialize tiptap JSON doc back to Jinja2 HTML/text.
 *
 * Walks the doc and replaces mergeField nodes with `{{ key }}`.
 */
export function tiptapToJinja(doc: JSONContent): string {
  return serializeNode(doc);
}

function serializeNode(node: JSONContent): string {
  if (node.type === 'mergeField') {
    return `{{ ${node.attrs?.key ?? ''} }}`;
  }

  if (node.type === 'text') {
    let text = node.text ?? '';
    // Apply marks
    if (node.marks) {
      const markRenderers: Record<string, (t: string, attrs?: Record<string, unknown>) => string> = {
        bold: (t) => `<strong>${t}</strong>`,
        italic: (t) => `<em>${t}</em>`,
        underline: (t) => `<u>${t}</u>`,
        link: (t, attrs) => `<a href="${(attrs?.href as string) ?? ''}">${t}</a>`,
      };
      node.marks.forEach((mark) => {
        const render = markRenderers[mark.type];
        if (render) {
          text = render(text, mark.attrs as Record<string, unknown> | undefined);
        }
      });
    }
    return text;
  }

  const children = (node.content ?? []).map(serializeNode).join('');

  switch (node.type) {
    case 'doc':
      return children;
    case 'paragraph':
      return children ? `<p>${children}</p>` : '<p></p>';
    case 'heading':
      return `<h${node.attrs?.level ?? 2}>${children}</h${node.attrs?.level ?? 2}>`;
    case 'bulletList':
      return `<ul>${children}</ul>`;
    case 'orderedList':
      return `<ol>${children}</ol>`;
    case 'listItem':
      return `<li>${children}</li>`;
    case 'hardBreak':
      return '<br>';
    default:
      return children;
  }
}

/**
 * Serialize tiptap JSON doc to plain text (for SMS).
 * Strips all HTML tags, replaces mergeField nodes with {{ key }}.
 */
export function tiptapToPlainText(doc: JSONContent): string {
  return serializePlainNode(doc);
}

function serializePlainNode(node: JSONContent): string {
  if (node.type === 'mergeField') {
    return `{{ ${node.attrs?.key ?? ''} }}`;
  }

  if (node.type === 'text') {
    return node.text ?? '';
  }

  const children = (node.content ?? []).map(serializePlainNode).join('');

  switch (node.type) {
    case 'paragraph':
      return `${children  }\n`;
    case 'hardBreak':
      return '\n';
    default:
      return children;
  }
}
